import { useGetBackListeElettorali } from '@framework/api-client';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
  RichTextAdvancedInput,
  RichTextAdvancedSchema,
  StyledDrawerCollapse,
  WidgetType,
  type IWidgetComponentProps,
} from '@framework/engine';
import { FormItem, MenuPresets, TextInput } from '@framework/form';
import { VerticalSpace } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Collapse } from 'antd';
import { useMemo } from 'react';
import { CandidateColumnSelectInput } from './config/CandidateColumnSelectInput';
import { ElectoralListSelectInput } from './config/ElectoralListSelectInput';
import { ConfigCardPanelContainer } from './ConfigCardPanelContainer';
import { validateLabelColumns } from './form/validateLabelColumns';
import type { ICandidateWidget } from './ICandidateWidget';

const { Panel } = Collapse;

export const CandidateWidgetConfigSchema: yup.ObjectSchema<ICandidateWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.Candidate).required(),
    panelTitle: RichTextAdvancedSchema.optional().label('Titolo del Pannello'),
    showPanel: yup.boolean().required().default(false),
    modalTitle: RichTextAdvancedSchema.optional().label(
      'Titolo della finestra modale di selezione',
    ),
    electoralListId: yup.string().label('Lista elettorale').required(),
    buttonText: yup.string().label('Testo del pulsante di scelta').optional(),
    buttonTextAfter: yup
      .string()
      .label('Testo del pulsante di modifica dopo la scelta')
      .optional(),
    columns: yup
      .array(yup.string().required())
      .default(['nome', 'cognome', 'dataNascita'])
      .min(1)
      .label('Colonne')
      .optional(),
    labelFieldColumns: yup
      .array(yup.string().required())
      .label('Colonne Etichetta')
      .when('columns', ([columns], schema) => {
        return schema.test({
          name: 'labelFieldColumns-in-columns',
          message:
            'Le colonne selezionate devono essere presenti tra le colonne visualizzate nella tabella.',
          test: function (labelFieldColumns) {
            const validation = validateLabelColumns(labelFieldColumns, columns);

            if (!validation.isValid) {
              return this.createError({ message: validation.message });
            }

            return true;
          },
        });
      })
      .optional(),
  })
    .required()
    .noUnknown();

export interface ICandidateWidgetConfigComponentProps
  extends IWidgetComponentProps<ICandidateWidget> {}

/**
 * Componente di configurazione del widget di selezione candidato
 */
export function CandidateWidgetConfigComponent(
  props: ICandidateWidgetConfigComponentProps,
) {
  const { data: electoralList } = useGetBackListeElettorali({
    isAttiva: true,
    isTornataAttiva: true,
  });

  const options = useMemo(
    () =>
      electoralList?.dati
        ?.map(type => {
          if (!type.id || !type.nome) return null;

          return {
            value: type.id,
            label: type.nome,
          };
        })
        .filter(el => el !== null) ?? [],
    [electoralList],
  );
  return (
    <StyledDrawerCollapse defaultActiveKey={['general', 'electoralList']} ghost>
      <Panel header="Impostazioni Generali" key="general">
        <CommonConfigComponent {...props} hideConfigDefaultValue />
      </Panel>
      <Panel header="Impostazioni Lista Elettorale" key="electoralList">
        <FormItem name="electoralListId" label="Lista elettorale">
          <ElectoralListSelectInput
            name="electoralListId"
            style={{ width: '100%' }}
            size="large"
            options={options}
          />
        </FormItem>
        <FormItem name="buttonText" label="Testo del pulsante di scelta">
          <TextInput
            name="buttonText"
            placeholder="Inserisci il testo del pulsante di scelta..."
          />
        </FormItem>
        <FormItem
          name="buttonTextAfter"
          label="Testo del pulsante di modifica dopo la scelta"
        >
          <TextInput
            name="buttonTextAfter"
            placeholder="Inserisci il testo del pulsante di modifica..."
          />
        </FormItem>
        <FormItem
          name="modalTitle"
          label="Titolo della finestra modale di selezione"
        >
          <RichTextAdvancedInput
            name="modalTitle"
            rows={2}
            menus={MenuPresets.Minimal}
          />
        </FormItem>

        {/* Card di configurazione pannello (bordo) con titolo */}
        <ConfigCardPanelContainer />

        <VerticalSpace size={12} />
        <Card
          size="small"
          title="Configura Colonne tabella di selezione Candidato e composizione Valore del campo dopo la selezione"
        >
          <CandidateColumnSelectInput
            name="columns"
            label="Campi da visualizzare nell'elenco"
            help="Seleziona le colonne da visualizzare nella tabella dei membri della lista elettorale."
            placeholder="Inserisci i campi da visualizzare..."
          />
          <CandidateColumnSelectInput
            name="labelFieldColumns"
            label="Campi che comporranno il valore"
            help="Seleziona le colonne che andranno a comporre il valore selezionato nel widget. Se non selezionate, verrà impostato il formato 'Nome Cognome Data di Nascita'"
            placeholder="Inserisci i campi che comporranno il valore..."
          />
        </Card>
      </Panel>
    </StyledDrawerCollapse>
  );
}
